import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  COMPLIANCE_DISCLAIMER_PARAGRAPHS,
  COMPLIANCE_DISCLAIMER_TITLE,
} from '../constants/disclaimer';
import { useComplianceDisclaimerAcceptance } from '../hooks/useComplianceDisclaimerAcceptance';

type Props = {
  children: ReactNode;
};

/**
 * Blocks Compliance routes for users until they accept the current
 * disclaimer version. Admins pass through immediately.
 */
export const RequireComplianceDisclaimer = ({ children }: Props) => {
  const navigate = useNavigate();
  const {
    isManager,
    roleLoading,
    isLoading,
    hasAccepted,
    error,
    accept,
    isAccepting,
  } = useComplianceDisclaimerAcceptance();

  if (roleLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        Loading...
      </div>
    );
  }

  if (!isManager) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        Loading...
      </div>
    );
  }

  if (hasAccepted) {
    return <>{children}</>;
  }

  const handleAgree = async () => {
    try {
      await accept();
    } catch {
      /* Error is surfaced via hook.error below. */
    }
  };

  const handleDecline = () => {
    navigate('/', { replace: true });
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="compliance-disclaimer-title"
      aria-describedby="compliance-disclaimer-body"
    >
      <div className="w-full max-w-xl space-y-6 rounded-lg border bg-card p-6 shadow-sm">
        <div className="space-y-2">
          <h1
            id="compliance-disclaimer-title"
            className="text-xl font-semibold tracking-tight"
          >
            {COMPLIANCE_DISCLAIMER_TITLE}
          </h1>
          <div
            id="compliance-disclaimer-body"
            className="space-y-3 text-sm text-muted-foreground"
          >
            {COMPLIANCE_DISCLAIMER_PARAGRAPHS.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </div>

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error instanceof Error
              ? error.message
              : 'Unable to save your acceptance. Please try again.'}
          </p>
        ) : null}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={handleDecline}
            disabled={isAccepting}
            aria-label="I do not agree to the compliance disclaimer"
          >
            I do not agree
          </Button>
          <Button
            type="button"
            onClick={handleAgree}
            disabled={isAccepting}
            aria-label="I agree to the compliance disclaimer"
          >
            {isAccepting ? 'Saving…' : 'I agree'}
          </Button>
        </div>
      </div>
    </div>
  );
};
